import React, { useState, useMemo } from "react";

// JobsPage.jsx
// Single-file React component for a Jobs Page with search, filters, sorting, pagination
// Uses inline styles + Bootstrap classes. Plug into any React app.

export default function JobsPage() {
  const roles = [
    "Full Stack Developer",
    "AI & ML Engineer",
    "Geotechnical Engineer",
    "Geologist",
    "Geophysicist",
    "Business Development Consultant",
    "Business Analyst",
    "Cloud Architect",
    "Quantum Computing Engineer",
    "Embedded Engineer",
    "IoT Engineer",
    "Robotics Engineer",
    "Energy Sector Specialist",
    "Environment & Sustainability Specialist",
    "Data Scientist",
    "Data Analyst",
    "DevOps Engineer",
    "AI Agents & LLM Developer",
    "SAP Technical",
    "SAP Functional",
    "Content Developer (EdTech)",
    "Technical Writer",
  ];

  // Sample job data — in a real app this would come from an API
  const sampleJobs = [
    {
      id: 1,
      title: "Full Stack Developer",
      role: "Full Stack Developer",
      location: "India | Remote",
      type: "Full-time",
      level: "Mid-Senior ",
      postedAt: "2025-07-30",
      salary: "₹12,00,000 - ₹20,00,000",
      skills: ["React", "Node.js", "Postgres", "AWS"],
      description:
        "Build and scale our core Web - 3.0 platform. Work closely with product and design to ship features in multi cloud with LLM models and Quantum algo's.",
    },
    {
      id: 2,
      title: "AI & ML Engineer - Geoscience Models",
      role: "AI & ML Engineer",
      location: "Remote",
      type: "Full-time",
      level: "Senior",
      postedAt: "2025-07-25",
      salary: "₹8,00,000 - ₹12,00,000",
      skills: ["Python", "PyTorch", "TensorFlow", "Geospatial"],
      description:
        "Develop ML models for seismic interpretation, remote sensing, and subsurface analytics.",
    },
    {
      id: 3,
      title: "Geotechnical Engineer (Site Ops)",
      role: "Geotechnical Engineer",
      location: "USA | India",
      type: "Full-Time",
      level: "Associate",
      postedAt: "2025-07-18",
      salary: "₹6,00,000 - ₹10,00,000",
      skills: ["Soil Testing", "PLAXIS", "Site Investigations"],
      description:
        "Lead site investigations and provide geotechnical recommendations for projects.",
    },
    {
      id: 4,
      title: "Cloud Architect (Multi-Cloud)",
      role: "Cloud Architect",
      location: "Mumbai, India",
      type: "Full-time",
      level: "Lead",
      postedAt: "2025-07-20",
      salary: "₹6,00,000 - ₹15,00,000",
      skills: ["AWS", "GCP", "Kubernetes", "Terraform"],
      description:
        "Design secure, scalable cloud architectures and CI/CD for multiple products.",
    },
    {
      id: 5,
      title: "Quantum Computing Research Engineer",
      role: "Quantum Computing Engineer",
      location: "Remote",
      type: "Full-time",
      level: "Research",
      postedAt: "2025-07-10",
      salary: "Competitive",
      skills: ["Qiskit", "Quantum Algorithms", "Python"],
      description:
        "Research and prototype quantum algorithms for optimization and inverse problems.",
    },
    {
      id: 6,
      title: "Embedded Engineer - Sensor Firmware",
      role: "Embedded Engineer",
      location: " India",
      type: "Full-time",
      level: "Mid",
      postedAt: "2025-06-30",
      salary: "₹8,00,000 - ₹15,00,000",
      skills: ["C", "C++", "RTOS", "ARM"],
      description: "Develop firmware for sensor nodes and low-power devices.",
    },
    {
      id: 7,
      title: "IoT Platform Engineer",
      role: "IoT Engineer",
      location: " India",
      type: "Full-time",
      level: "Mid",
      postedAt: "2025-07-05",
      salary: "₹10,00,000 - ₹18,00,000",
      skills: ["MQTT", "AWS IoT", "Node.js"],
      description: "Build and maintain our IoT ingestion & device management platform.",
    },
    {
      id: 8,
      title: "Business Development Consultant - Energy",
      role: "Business Development Consultant",
      location: "India",
      type: "Contract",
      level: "Senior",
      postedAt: "2025-07-01",
      salary: "Commission + Base",
      skills: ["Sales", "Energy Market", "Partnerships"],
      description: "Drive partnerships and enterprise sales in the energy sector.",
    },
    {
      id: 9,
      title: "Content Developer - EdTech (Geoscience)",
      role: "Content Developer (EdTech)",
      location: "Remote - internship",
      type: "Part-time",
      level: "Associate",
      postedAt: "2025-06-15",
      salary: "₹15,000 - ₹30,000 / month",
      skills: ["Curriculum", "Geo Science", "Instructional Design"],
      description: "Create course materials, quizzes, and hands-on labs for geoscience learners.",
    },
    {
      id: 10,
      title: "AI Agents & LLM Developer",
      role: "AI Agents & LLM Developer",
      location: "Remote",
      type: "Full-time",
      level: "Senior",
      postedAt: "2025-07-02",
      salary: "₹15,00,000 - ₹22,00,000",
      skills: ["LLMs", "Prompt Engineering", "RAG", "Vector DBs"],
      description: "Build AI agents, retrieval systems and integrate LLMs into products.",
    },
    // additional roles can be added programmatically or fetched from API
  ];

  const [query, setQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [appliedJob, setAppliedJob] = useState(null);

  // Inline styles
  const styles = {
    page: { padding: "30px 15px", fontFamily: "Arial, sans-serif" },
    header: { marginBottom: 20 },
    searchBox: { display: "flex", gap: 8, alignItems: "center" },
    chip: (active) => ({
      padding: "6px 12px",
      borderRadius: 20,
      cursor: "pointer",
      border: active ? "2px solid #0d6efd" : "1px solid #ddd",
      background: active ? "rgba(13,110,253,0.08)" : "white",
    }),
    cardDesc: { fontSize: 14, color: "#444" },
    footer: { marginTop: 20, textAlign: "center", color: "#666" },
  };

  // Filter + search logic
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = sampleJobs.slice();

    if (selectedRole !== "All") {
      list = list.filter((j) => j.role === selectedRole);
    }

    if (q.length > 0) {
      list = list.filter((j) => {
        const inTitle = j.title.toLowerCase().includes(q);
        const inLoc = j.location.toLowerCase().includes(q);
        const inSkills = j.skills.join(" ").toLowerCase().includes(q);
        const inDesc = j.description.toLowerCase().includes(q);
        return inTitle || inLoc || inSkills || inDesc;
      });
    }

    // sorting
    if (sortBy === "newest") {
      list.sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
    } else if (sortBy === "oldest") {
      list.sort((a, b) => new Date(a.postedAt) - new Date(b.postedAt));
    } else if (sortBy === "salary") {
      // best-effort: we won't parse salaries fully here; keep original order
    }

    return list;
  }, [query, selectedRole, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function clearFilters() {
    setQuery("");
    setSelectedRole("All");
    setSortBy("newest");
    setCurrentPage(1);
  }

  function openApply(job) {
    setAppliedJob(job);
  }

  function closeApply() {
    setAppliedJob(null);
  }

  function submitApplication(e) {
    e.preventDefault();
    // In a real app, you'd POST to an API. Here we simulate success and close modal.
    alert("Application submitted for: " + (appliedJob?.title || ""));
    closeApply();
  }

  return (
    <div style={styles.page} className="container my-5 py-5">
      <div style={styles.header} className="d-flex justify-content-between align-items-center">
        <div>
          <h2 className="mb-1">Careers at Olewin Academy / Olewin Tech / Geo-Intelligence / other Product & Services</h2>
          <div style={{ color: "#666" }}>Open roles that accelerate our growth across technology, geoscience, energy and ed-tech.</div>
        </div>
        <div>
          <button className="btn btn-primary" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="row mb-3">
        <div className="col-md-8">
          <div style={styles.searchBox}>
            <input
              className="form-control"
              placeholder="Search by role, skill, location or keyword (eg. React, remote, seismic)"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
        <div className="col-md-4 d-flex gap-2 justify-content-end">
          <select className="form-select w-auto" value={selectedRole} onChange={(e) => { setSelectedRole(e.target.value); setCurrentPage(1); }}>
            <option value="All">All Roles</option>
            {roles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          <select className="form-select w-auto" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="salary">Salary (Best)</option>
          </select>
        </div>
      </div>

      {/* Role chips */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        <div
          onClick={() => setSelectedRole("All")}
          style={styles.chip(selectedRole === "All")}
          className="me-1"
        >
          All
        </div>
        {roles.map((r) => (
          <div
            key={r}
            onClick={() => { setSelectedRole(r); setCurrentPage(1); }}
            style={styles.chip(selectedRole === r)}
            title={r}
          >
            {r}
          </div>
        ))}
      </div>

      {/* Jobs list */}
      <div className="row">
        <div className="col-lg-8">
          {pageItems.length === 0 ? (
            <div className="alert alert-warning">No roles found. Try different keywords or clear filters.</div>
          ) : (
            pageItems.map((job) => (
              <div className="card mb-3" key={job.id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5 className="card-title mb-1">{job.title}</h5>
                      <div style={{ fontSize: 13, color: "#666" }}>{job.location} • {job.type} • {job.level}</div>
                    </div>
                    <div className="text-end">
                      <div style={{ fontWeight: 600 }}>{job.salary}</div>
                      <div style={{ fontSize: 13, color: "#888" }}>Posted: {job.postedAt}</div>
                    </div>
                  </div>

                  <p style={styles.cardDesc} className="mt-2">{job.description}</p>

                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {job.skills.map((s) => (
                      <span key={s} className="badge bg-light text-dark border">{s}</span>
                    ))}
                  </div>

                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <div>
                      <button className="btn btn-primary btn-sm me-2" onClick={() => openApply(job)}>Apply</button>
                      <button className="btn btn-outline-secondary btn-sm">Save</button>
                    </div>
                    <div>
                      <small style={{ color: "#666" }}>Role: {job.role}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          <nav aria-label="Jobs pagination">
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</button>
              </li>
              {Array.from({ length: totalPages }).map((_, i) => (
                <li className={`page-item ${currentPage === i + 1 ? "active" : ""}`} key={i}>
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Right column - Quick stats / CTA / Filters */}
        <div className="col-lg-4">
          <div className="card mb-3 p-3">
            <h6>Quick Stats</h6>
            <div className="d-flex justify-content-between">
              <div>Total roles</div>
              <div><strong>{sampleJobs.length}</strong></div>
            </div>
            <div className="d-flex justify-content-between mt-2">
              <div>Matching</div>
              <div><strong>{filtered.length}</strong></div>
            </div>

            <hr />
            <h6>Want to hire?</h6>
            <p style={{ fontSize: 14, color: "#555" }}>Post a role to attract top candidates across tech, geoscience, and energy.</p>
            <button className="btn btn-success btn-sm w-100">Post a Job</button>
          </div>

          <div className="card p-3">
            <h6>Filter by skill</h6>
            <p style={{ fontSize: 13, color: "#555" }}>Type a skill in the search box above (eg. React, Qiskit).</p>

            <div className="mt-2">
              {Array.from(new Set(sampleJobs.flatMap((j) => j.skills))).map((s) => (
                <button
                  key={s}
                  className="btn btn-outline-secondary btn-sm me-2 mb-2"
                  onClick={() => { setQuery(s); setCurrentPage(1); }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>


      {/* Apply Modal (simple) */}
      {appliedJob && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Apply: {appliedJob.title}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeApply}></button>
              </div>
              <form onSubmit={submitApplication}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Full name</label>
                    <input className="form-control" required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Short message</label>
                    <textarea className="form-control" rows={3} placeholder={`Why are you a good fit for ${appliedJob.title}?`} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeApply}>Close</button>
                  <button type="submit" className="btn btn-primary">Submit Application</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
